import React, { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import type { SelectItemMode } from '@atlaskit/editor-common/type-ahead';
import type { ExtractInjectionAPI, TypeAheadHandler } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingDialogZIndex } from '@atlaskit/editor-shared-styles/constants';
import { Box } from '@atlaskit/primitives/compiled';

import type { CloseSelectionOptions } from '../../pm-plugins/constants';
import type { TypeAheadPlugin } from '../../typeAheadPluginType';
import { InputQuery } from '../InputQuery';

import { buildTypeAheadMenuModel } from './buildTypeAheadMenuModel';
import { getTypeAheadItemId } from './getTypeAheadItemId';
import { PassThrough } from './PassThrough';
import { TypeAheadItemRow } from './TypeAheadItemRow';
import { TypeAheadProvider } from './TypeAheadProvider';
import type { TypeAheadSurface } from './typeAheadSurfaces';

const POPUP_OFFSET = [0, 8];

type Props = {
	anchorElement: HTMLElement;
	api: ExtractInjectionAPI<TypeAheadPlugin> | undefined;
	cancel: (params: {
		addPrefixTrigger: boolean;
		forceFocusOnEditor: boolean;
		setSelectionAt: CloseSelectionOptions;
		text: string;
	}) => void;
	editorView: EditorView;
	forceFocus: boolean;
	onClose: () => void;
	onUndoRedo?: (inputType: 'historyUndo' | 'historyRedo') => boolean;
	popupsBoundariesElement?: HTMLElement;
	popupsMountPoint?: HTMLElement;
	popupsScrollableElement?: HTMLElement;
	query: string;
	reopenQuery?: string;
	setQuery: (query: string) => void;
	surface: TypeAheadSurface;
	triggerHandler: TypeAheadHandler;
};

export const RegisteredTypeAheadMenu = ({
	anchorElement,
	api,
	cancel,
	editorView,
	forceFocus,
	onClose,
	onUndoRedo,
	popupsBoundariesElement,
	popupsMountPoint,
	popupsScrollableElement,
	query,
	reopenQuery,
	setQuery,
	surface,
	triggerHandler,
}: Props): React.JSX.Element => {
	const intl = useIntl();
	const generatedId = useId();
	const listId = `${surface.listIdPrefix}-${generatedId}`;
	const menuRef = useRef<HTMLDivElement>(null);
	const [selectedItemIndex, setSelectedItemIndex] = useState(0);
	const components = useMemo(
		() => api?.uiControlRegistry?.actions.getComponents(surface.root.key) ?? [],
		[api, surface.root.key],
	);
	const model = useMemo(
		() => buildTypeAheadMenuModel(components, surface.root),
		[components, surface.root],
	);
	const itemKeySignature = model.items.map(({ key }) => key).join(',');

	useLayoutEffect(() => {
		setSelectedItemIndex(model.items.length > 0 ? 0 : -1);
	}, [itemKeySignature, model.items.length, query]);

	const selectActiveItem = useCallback(
		(_mode: SelectItemMode) => {
			if (selectedItemIndex < 0) {
				return;
			}

			menuRef.current?.ownerDocument
				.getElementById(getTypeAheadItemId(listId, selectedItemIndex))
				?.click();
		},
		[listId, selectedItemIndex],
	);
	const selectNextItem = useCallback(() => {
		setSelectedItemIndex((currentIndex) => {
			if (model.items.length === 0) {
				return -1;
			}

			return currentIndex < 0 || currentIndex === model.items.length - 1 ? 0 : currentIndex + 1;
		});
	}, [model.items.length]);
	const selectPreviousItem = useCallback(() => {
		setSelectedItemIndex((currentIndex) => {
			if (model.items.length === 0) {
				return -1;
			}

			return currentIndex <= 0 ? model.items.length - 1 : currentIndex - 1;
		});
	}, [model.items.length]);
	const typeAheadContextValue = useMemo(
		() => ({
			api,
			editorView,
			inputMethod: surface.inputMethod,
			onClose,
			query,
			triggerHandler,
		}),
		[api, editorView, onClose, query, surface.inputMethod, triggerHandler],
	);
	const noOp = useCallback(() => {}, []);
	const RootComponent = model.root?.component ?? PassThrough;
	const SurfaceProvider = surface.Provider;

	return (
		<>
			<InputQuery
				activeDescendantId={
					selectedItemIndex >= 0 ? getTypeAheadItemId(listId, selectedItemIndex) : undefined
				}
				cancel={cancel}
				editorView={editorView}
				forceFocus={forceFocus}
				listId={listId}
				onItemSelect={selectActiveItem}
				onQueryChange={setQuery}
				onQueryFocus={noOp}
				onUndoRedo={onUndoRedo}
				optionCount={model.items.length}
				reopenQuery={reopenQuery}
				selectNextItem={selectNextItem}
				selectPreviousItem={selectPreviousItem}
				shouldKeepOpenOnRegisteredMenu={true}
				triggerQueryPrefix={triggerHandler.trigger}
			/>
			<Popup
				ariaLabel={null}
				boundariesElement={popupsBoundariesElement}
				fitWidth={320}
				mountTo={popupsMountPoint}
				offset={POPUP_OFFSET}
				preventOverflow={true}
				scrollableElement={popupsScrollableElement}
				target={anchorElement}
				zIndex={akEditorFloatingDialogZIndex}
			>
				<Box
					ref={menuRef}
					backgroundColor="elevation.surface.overlay"
					data-registered-type-ahead-menu=""
					padding="space.050"
				>
					<TypeAheadProvider value={typeAheadContextValue}>
						<SurfaceProvider>
							<RootComponent>
								<Box aria-label={intl.formatMessage(surface.listLabel)} id={listId} role="listbox">
									{model.items.map((registration, itemIndex) => (
										<TypeAheadItemRow
											isSelected={selectedItemIndex === itemIndex}
											itemIndex={itemIndex}
											Item={surface.Item}
											key={`${registration.type}:${registration.key}`}
											listId={listId}
											onItemHover={setSelectedItemIndex}
											registration={registration}
										/>
									))}
								</Box>
							</RootComponent>
						</SurfaceProvider>
					</TypeAheadProvider>
				</Box>
			</Popup>
		</>
	);
};
